/**
 * مؤقّت العمل.
 *
 * قيدُ وقتٍ على سجل: مهمة، أو عميل، أو عميل محتمل. يبدأ ويقف، أو يُكتب
 * يدويًّا لمن نسي أن يبدأه.
 *
 * ## مؤقّت واحد يعمل لكل مستخدم
 *
 * لا أحد يعمل على شيئين في اللحظة نفسها. ومؤقّتان يعملان معًا يجعلان
 * مجموع اليوم أكبر من اليوم — وهو رقمٌ لا يُبنى عليه شيء. فبدءُ مؤقّت
 * يوقف السابق ويُخبر بذلك صراحةً.
 *
 * ## السقف يمنع الرقم المستحيل
 *
 * مؤقّتٌ نُسي مفتوحًا ليلة الخميس يُنتج سبعين ساعة عملٍ يوم الأحد، فيسمّم
 * كل تقريرٍ يقرؤه بعد ذلك. والحدّ هنا لا يُخفي النسيان بل يعلنه: ما تجاوز
 * السقف يُقصّ إليه ويُعلَّم `clipped`، فيُعرف أن الرقم مقصوص لا مقيس.
 *
 * وليس مجدولًا يُغلق المؤقّتات في الخلفية: القصّ يقع لحظة الإيقاف، ولا
 * يُكتب شيء قبله — فمؤقّتٌ منسيّ لا يفسد شيئًا حتى يُوقَف.
 */
import type { Labelled } from './common.js';

/* ==========================================================================
   الكيانات
   ========================================================================== */

/**
 * ما يُقاس عليه الوقت.
 *
 * قائمة مغلقة كسائر الكتالوجات: بدونها يستطيع من يُرسل طلبًا مباشرًا أن
 * يقيس وقتًا على أي جدول باسمه، فتصير التقارير تجمع أوقاتًا لا سياق لها.
 */
export const TIMER_ENTITIES = ['activity', 'lead', 'account'] as const;
export type TimerEntity = (typeof TIMER_ENTITIES)[number];

export interface TimerEntityDef extends Labelled<TimerEntity> {
  singular: string;
}

export const TIMER_ENTITY_DEFS: readonly TimerEntityDef[] = [
  { key: 'activity', label: 'الأنشطة', singular: 'النشاط', kind: 'info' },
  { key: 'lead', label: 'العملاء المحتملون', singular: 'العميل المحتمل', kind: 'neutral' },
  { key: 'account', label: 'العملاء', singular: 'العميل', kind: 'brand' },
] as const;

export const TIMER_ENTITY_MAP = Object.fromEntries(
  TIMER_ENTITY_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<TimerEntity, TimerEntityDef>>;

/* ==========================================================================
   الحدود
   ========================================================================== */

/** أقصى ما يُسجَّل للقيد الواحد بالساعات. */
export const MAX_TIMER_HOURS = 12;
export const MAX_TIMER_SECONDS = MAX_TIMER_HOURS * 3600;

/**
 * أدنى ما يُسجَّل بالثواني.
 *
 * ضغطةٌ على «ابدأ» ثم «أوقف» تُنتج قيدًا بثانيتين يشغل سطرًا في السجل ولا
 * يقول شيئًا. وأقلّ من دقيقة لا يُحفظ أصلًا.
 */
export const MIN_TIMER_SECONDS = 60;

/** أقصى ما يُكتب يدويًّا في اليوم الواحد بالدقائق. */
export const MAX_MANUAL_MINUTES = MAX_TIMER_HOURS * 60;

/** كم يومًا إلى الوراء يُقبل القيد اليدوي. */
export const MANUAL_BACKDATE_DAYS = 14;

/* ==========================================================================
   الشكل المعاد
   ========================================================================== */

export interface TimeEntryView {
  id: string;
  entity: TimerEntity;
  entityId: string;
  /** اسم السجل، ليُقرأ السطر بلا طلبٍ ثانٍ. */
  entityName: string | null;
  userId: string;
  userName: string | null;
  startedAt: string;
  endedAt: string | null;
  /** المحسوبة عند الإيقاف؛ فارغة ما دام يعمل. */
  seconds: number | null;
  note: string | null;
  /** قُصّ إلى السقف: الرقم مقصوص لا مقيس. */
  clipped: boolean;
  /** كُتب يدويًّا لا بمؤقّت. */
  manual: boolean;
}

export interface RunningTimer {
  entry: TimeEntryView | null;
  /** ما مضى بالثواني، محسوبًا في الخادم — ساعةُ الجهاز قد تكون منحرفة. */
  elapsed: number;
}

export interface TimerSummary {
  todaySeconds: number;
  weekSeconds: number;
  running: RunningTimer;
}

/* ==========================================================================
   الحساب والعرض
   ========================================================================== */

/**
 * ما مضى من مؤقّت يعمل، مقصوصًا عند السقف.
 *
 * دالة واحدة يستعملها الخادم عند الإيقاف والواجهة أثناء العدّ: لو حسبت
 * الواجهة بلا قصّ لرأى المستخدم عدّادًا يمضي ثم رقمًا محفوظًا أصغر منه،
 * فيظنّ أن وقته ضاع.
 */
export function elapsedSeconds(startedAt: string | Date, now: Date = new Date()): number {
  const start = typeof startedAt === 'string' ? new Date(startedAt) : startedAt;
  const raw = Math.floor((now.getTime() - start.getTime()) / 1000);
  return Math.max(0, Math.min(raw, MAX_TIMER_SECONDS));
}

/** هل تجاوز المؤقّت سقفه — فما يُعرض مقصوص. */
export function isClipped(startedAt: string | Date, now: Date = new Date()): boolean {
  const start = typeof startedAt === 'string' ? new Date(startedAt) : startedAt;
  return Math.floor((now.getTime() - start.getTime()) / 1000) > MAX_TIMER_SECONDS;
}

/**
 * الوقت عدّادًا: `س:دد:ثث`.
 *
 * للعدّاد الذي يمضي أمام العين — الثواني فيه مقصودة لأنها الدليل على أنه
 * يعمل. أما المجاميع فتُكتب بالعربية.
 */
export function clock(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(h)}:${pad(m)}:${pad(sec)}`;
}

/**
 * الوقت جملةً عربية: «ساعتان و٣٠ دقيقة».
 *
 * للمجاميع في التقارير والملخّصات. و«٩٠ دقيقة» صحيحٌ حسابيًّا ولا يُقرأ:
 * من يقرأ تقريرًا يريد أن يعرف كم ساعة عمل عليه لا كم دقيقة.
 */
export function duration(seconds: number): string {
  const total = Math.max(0, Math.round(seconds / 60));
  if (total === 0) return 'أقل من دقيقة';

  const h = Math.floor(total / 60);
  const m = total % 60;

  const hours =
    h === 0 ? '' : h === 1 ? 'ساعة' : h === 2 ? 'ساعتان' : h <= 10 ? `${h} ساعات` : `${h} ساعة`;
  const mins =
    m === 0 ? '' : m === 1 ? 'دقيقة' : m === 2 ? 'دقيقتان' : m <= 10 ? `${m} دقائق` : `${m} دقيقة`;

  if (hours && mins) return `${hours} و${mins}`;
  return hours || mins;
}
